import { PropTypes } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Tooltip, Box, Button, withStyles, CircularProgress } from '@material-ui/core';

import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import FormInputWithOnChange from '@/component/common/form/input-with-onchange';
import { handleCreateTmpPreviewHtmlCdn, readHtmlFromUrl } from '../handler/HtmlFieldHandler';
import trI18n from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';

export default function HtmlFieldUI(props) {
    useTranslationSubscription();
    const defaultHtmlUrl = props?.defaultValue || '';

    const [previewSrc, setPreviewSrc] = useState(defaultHtmlUrl);
    const [htmlEditValue, setHtmlEditValue] = useState('');
    const [isEditedHtml, setIsEditedHtml] = useState(false);
    const [isLoadingHtml, setIsLoadingHtml] = useState(false);

    const [isPreviewMode, setIsPreviewMode] = useState(true);
    const [isShowFlagMessage, setIsShowFlagMessage] = useState(false);

    const FlagMessageCopyLink = withStyles({
        tooltip: {
            color: '#1DA578',
            backgroundColor: '#D7F4E2',
            padding: '8px 16px',
            fontSize: '14px',
            boxShadow: '6px 5px 5px -4px rgba(0,0,0,0.3)'
        },
        arrow: {
            color: '#D7F4E2'
        }
    })(Tooltip);

    const publicForCustomer = props.data.structure.publicForCustomer;
    const isAddNewTicket = !props?.detailTicket;
    const isViewOnly =
        Number(publicForCustomer) === Constant.SETTING_FORM_DEFAULT.READ_ONLY ||
        props.viewFromProjectOfEscalateTicket ||
        !Utils.isEmptyString(props.detailTicket?.deletedAt) ||
        props.hasEditFieldPermissions === false
    const isDefaultViewModeInCreateScreen =
        isAddNewTicket && Utils.isEmptyString(defaultHtmlUrl) && Utils.isEmptyString(htmlEditValue);

    const handleChangeHtml = e => {
        if (isViewOnly) return;
        if (props?.setIsEdited) {
            props?.setIsEdited(true);
            props?.setShowButtonEditTicket && props?.setShowButtonEditTicket();
            if (!props?.listFieldEdited?.includes(props?.name)) {
                props?.setListFieldEdited([...props.listFieldEdited, props?.name]);
            }
            if (props?.setOnChangeEdit) {
                props?.setOnChangeEdit(!props.onChangeEdit);
            }
        }
        setIsEditedHtml(true);
        setHtmlEditValue(e.target.value);
    };

    const handleResetEditedData = async () => {
        if (isViewOnly) return;
        props?.clearErrors(props?.name);
        const oldHtmlUrl = defaultHtmlUrl;

        setIsLoadingHtml(true);
        setIsEditedHtml(false);
        
        const oldHtml = await readHtmlFromUrl(oldHtmlUrl);
        if (oldHtml) {
            props?.setValue(props?.name, oldHtml);
            setHtmlEditValue(oldHtml);
            setPreviewSrc(oldHtmlUrl);
        } else {
            props?.setValue(props?.name, '');
            setHtmlEditValue('');
            setPreviewSrc('');
        }
        setIsPreviewMode(true);
        setIsLoadingHtml(false);
    };

    const getHtmlS3Link = () => {
        const ticketDetail = props?.detailTicket;

        if (!ticketDetail || Utils.isEmptyObject(ticketDetail) || !ticketDetail?.id) return;

        let currentFieldId = props.name;
        if (!currentFieldId) return;

        let ticketId = ticketDetail.id
        if (ticketDetail.ticketType === Constant.TICKET_TYPE.BROADCAST_CHILD) {
            ticketId = ticketDetail.parent;
        }

        return Utils.buildHtmlShareLinkPage(ticketId, currentFieldId)
    }

    const handleCopyLinkS3 = () => {
        let s3Link = getHtmlS3Link();
        if (!s3Link) return;

        navigator.clipboard
            .writeText(s3Link)
            .then(() => {
                setIsShowFlagMessage(true);
                setTimeout(() => {
                    setIsShowFlagMessage(false);
                }, 1500);
            })
            .catch(err => {console.log(`handleCopyLinkS3 err`, err)});
    }

    const handleOpenNewTab = () => {
        let s3Link = getHtmlS3Link();
        if (!s3Link) return;
        window.open(s3Link, '_blank').focus();
    };

    const handleSaveEditedHtml = async () => {
        if (isViewOnly) return;
        if (props?.errorlist.hasOwnProperty(props?.name)) return;
        
        if (isEditedHtml) {
            try {
                setIsLoadingHtml(true);
                const previewCdn = await handleCreateTmpPreviewHtmlCdn(htmlEditValue, props?.name);
                if (previewCdn) {
                    setPreviewSrc(previewCdn);
                }
            } catch (error) {
                console.log('create tmp err:', error);
            } finally {
                setIsEditedHtml(false);
                setIsLoadingHtml(false);
            }
        }

        setIsPreviewMode(prev => !prev);
    }

    // uef run when init / change tab ticket
    useEffect(() => {
        setIsPreviewMode(!isAddNewTicket);
    }, [props?.detailTicket]);

    // uef run when init / change selectedForm
    useEffect(() => {
        let isKnowledgeForm = [Constant.FORM_TYPE.FORM_KNOWLEDGE, Constant.FORM_TYPE.FORM_MASTER].includes(props?.parentForm?.type);
        if (isAddNewTicket || props?.isDraftMode || (props?.editTicket && !isKnowledgeForm)) {
            props.register(props?.name, {
                onChange: e => {}
            });

            setIsLoadingHtml(true);
            readHtmlFromUrl(defaultHtmlUrl)
                .then(htmlText => {
                    setPreviewSrc(defaultHtmlUrl);
                    props?.setValue(props?.name, htmlText || '');
                    setHtmlEditValue(htmlText || '');
                })
                .finally(() => { setIsLoadingHtml(false) })
        }
    }, [props?.parentForm]);
    
    useEffect(() => {
        if (props?.flagReset || props?.displayMode === Constant.DISPLAY_MODE.VIEW) {
            props?.clearErrors(props?.name);

            setIsLoadingHtml(true);
            readHtmlFromUrl(defaultHtmlUrl)
                .then(htmlText => {
                    setPreviewSrc(defaultHtmlUrl);
                    props?.setValue(props?.name, htmlText || '');
                    setHtmlEditValue(htmlText || '');
                    setIsPreviewMode(true);
                    props.setFlagReset(false);
                })
                .finally(() => { setIsLoadingHtml(false) })
        }
    }, [props?.flagReset, props?.displayMode, props?.defaultValue]);

    // uef run when change tab => create tmp src
    useEffect(() => {
        if (!props?.tabId || !props?.defaultValue) return;
        const changedTabHtmlValue = props.defaultValue;
        try {
            const htmlUrl = new URL(changedTabHtmlValue);
            readHtmlFromUrl(htmlUrl)
                .then(htmlText => {
                    setPreviewSrc(htmlUrl);
                    setHtmlEditValue(htmlText || '');
                    props?.setValue(props?.name, htmlText || '');
                })
                .catch(err => console.log('change tab Storage.put err ', err))
                .finally(() => { setIsLoadingHtml(false) })
        } catch (error) {
            // handle when htmlUrl is not cdn preview
            setIsLoadingHtml(true);
            handleCreateTmpPreviewHtmlCdn(changedTabHtmlValue, props?.name)
                .then(previewCdn => {
                    if (previewCdn) {
                        setPreviewSrc(previewCdn);
                        setHtmlEditValue(changedTabHtmlValue);
                        props?.setValue(props?.name, changedTabHtmlValue || '');
                    }
                })
                .catch(err => {
                    console.log('change tab Storage.put err ', err)
                })
                .finally(() => {
                    setIsEditedHtml(false);
                    setIsLoadingHtml(false);
                })
        }
    }, [props?.tabId, props?.defaultValue]);

    return (
        <div className="form-addTicket field-">
            <div className="cell-prod-status gap-8">
                {props?.isTooltipTitle ? (
                    <div className="d-flex">
                        <Tooltip title={props?.label}>
                            <span className="form-addTicket-label text-truncate">
                                <label
                                    className={
                                        `text-truncate line-height-18 w-f100 ${
                                            (publicForCustomer
                                                ? 'form-addTicket-label-color'
                                                : 'form-addTicket-label')
                                        }`
                                    }
                                >
                                    {props?.label}
                                </label>
                            </span>
                        </Tooltip>
                        {props.data.structure.required && (
                            <span className="required padding-left-4">*</span>
                        )}
                    </div>
                ) : (
                    <label
                        className={
                            `w-f100 ${
                            publicForCustomer
                                ? 'form-addTicket-label-color'
                                : 'form-addTicket-label'
                            }`
                        }
                    >
                        {props?.label}{' '}
                        {props.data.structure.required && <span className="required">*</span>}
                    </label>
                )}

                {props?.isShowIconCopyFieldId && (
                    <Tooltip title={props?.data.id} placement="top" arrow>
                        <img
                            src="/assets/image/icon-copy.svg"
                            alt="copy"
                            onClick={() => navigator.clipboard.writeText(props?.data.id)}
                        />
                    </Tooltip>
                )}
            </div>

            <div className="form-addTicket-input">
                {/* Preview Mode */}
                <Box style={{ display: isPreviewMode ? 'block' : 'none' }}>
                    <Box style={{ display: 'flex', position: 'relative'}}>
                        {/* View HTML render */}
                        {isPreviewMode && (
                            <iframe
                                title="html-content"
                                style={{
                                    flex: 1,
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    overflowY: 'auto',
                                    minHeight: '150px',
                                    maxHeight: '500px',
                                    height: `${isDefaultViewModeInCreateScreen ? "auto" : "500px"}`,
                                    backgroundColor:`${isDefaultViewModeInCreateScreen ? "#F2F3F5" : "transparent"}`,
                                    width:'100%'
                                }}
                                src={previewSrc}
                                allow={Constant.IFRAME_ALLOW_PERMISSIONS}
                                allowfullscreen
                                className={isViewOnly ? 'html-disabled' : ''}
                                role="document"
                                aria-label={`Preview of ${props?.label || 'HTML content'}`}
                            />
                        )}

                        {/* HTML options */}
                        <div
                            style={{
                                display: 'inline-flex',
                                flexWrap: 'wrap',
                                alignItems: 'flex-start',
                                maxWidth: '88px',
                                alignContent: 'start'
                        }}>
                            {!isAddNewTicket && (
                                <>
                                    <Button className="button-html html-link cursor-default" onClick={handleCopyLinkS3}>
                                        <FlagMessageCopyLink
                                            title={trI18n('KNOWDEMSG002')}
                                            placement="top-start"
                                            arrow={true}
                                            open={isShowFlagMessage}
                                        >
                                            <img src="/assets/image/html-link-icon.svg" alt="" className="cursor-pointer" />
                                        </FlagMessageCopyLink>
                                    </Button>
                                    <Button className="button-html html-blank cursor-default" onClick={handleOpenNewTab}>
                                        <img src="/assets/image/html-open-new-tab-icon.svg" alt="" className="cursor-pointer" />
                                    </Button>
                                </>
                            )}
                            {!isViewOnly && (
                                <Button
                                    className="button-html cursor-default"
                                    disabled={isViewOnly}
                                    onClick={() => {
                                        if (isViewOnly) return;
                                        setIsPreviewMode(prev => !prev);
                                    }
                                }>
                                    <img src="/assets/image/button-url-edit.svg" alt="" className="cursor-pointer" />
                                </Button>
                            )}
                        </div>
                    </Box>

                </Box>

                {/* Edit Mode */}
                <Box style={{ display: !isPreviewMode ? 'block' : 'none' }}>
                    <Box display="flex" sx={{ width: '100%', position: 'relative' }}>
                        <Box sx={{ flexGrow: 1, paddingRight: '10px' }}>
                            <FormInputWithOnChange
                                name={props?.name}
                                className="form-control form-control-wrapTextarea form-control-hg200"
                                variant="outlined"
                                defaultValue={htmlEditValue}
                                value={htmlEditValue}
                                multiline
                                disabled={isViewOnly}
                                errorlist={props?.errorlist}
                                autoComplete="off"
                                required={props?.required}
                                onChange={handleChangeHtml}
                                data-cy="text-area-input-field"
                                isDisableOnBlur={props.isDisableOnBlur ? props.isDisableOnBlur : true}
                            />

                        </Box>
                        <Box display="inline-flex" alignItems="flex-start">
                            <Button
                                className="button-mermaid cursor-default"
                                onClick={async() => await handleSaveEditedHtml()}
                            >
                                <img src="/assets/image/button-url-apply.svg" alt="" className="cursor-pointer" />
                            </Button>
                            <Button
                                className="button-mermaid cursor-default"
                                onClick={async () => await handleResetEditedData()}
                                style={{ paddingRight: '0px' }}
                            >
                                <img src="/assets/image/button-url-reset.svg" alt="" className="cursor-pointer" />
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </div>

            {isLoadingHtml && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: 'rgba(255,255,255,0.7)',
                        zIndex: 1
                    }}
                >
                    <CircularProgress />
                </Box>
            )}
        </div>
    );
};

HtmlFieldUI.propTypes = {
    name: PropTypes.string,
    label: PropTypes.string,
    defaultValue: PropTypes.string,
    data: PropTypes.any,
    detailTicket: PropTypes.any,
    parentForm: PropTypes.any,
    errorlist: PropTypes.any,
    flagReset: PropTypes.any,
    displayMode: PropTypes.any,
    tabId: PropTypes.any,
    isTooltipTitle: PropTypes.any,
    isShowIconCopyFieldId: PropTypes.any,
    hasEditFieldPermissions: PropTypes.any,
    viewFromProjectOfEscalateTicket: PropTypes.any,
    isDraftMode: PropTypes.any,
    editTicket: PropTypes.any,
    required: PropTypes.any,
    isDisableOnBlur: PropTypes.any,
    setIsEdited: PropTypes.any,
    setShowButtonEditTicket: PropTypes.any,
    setListFieldEdited: PropTypes.any,
    setOnChangeEdit: PropTypes.any,
    setFlagReset: PropTypes.any,
    listFieldEdited: PropTypes.any,
    onChangeEdit: PropTypes.any,
    setValue: PropTypes.any,
    clearErrors: PropTypes.any,
    register: PropTypes.any
};
